import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { HipocratesImportService } from 'src/app/services/entidades/hipocrates-import.service';
import { SignoPadronesService } from 'src/app/services/entidades/signo-padrones.service';
import Swal from 'sweetalert2';

@Component({ selector: 'app-hipocrates-import', templateUrl: './hipocrates-import.component.html', styleUrls: ['./hipocrates-import.component.scss'] })
export class HipocratesImportComponent implements OnInit, OnDestroy {
  meta: any; job: any; report: any; busy = false; message = ''; error = ''; terms: string[] = []; names: string[] = []; locations: string[] = [];
  file: File | null = null; complement: File | null = null; filter = ''; page = 0; pageSize = 25; geoRunning = false; stopGeo = false; geoProgress = 0;
  destroyed = false;
  configured = false; provinceReviewed = false; locationsReviewed = false;
  coverage: any = null;
  newNames: { [term: string]: string } = {};
  renames: { [name: string]: string } = {};
  suggestions: { [term: string]: { _id: string; concepto: string } } = {};
  centerSuggestions: { [key: string]: Array<{ term: string; id: string }> } = {};
  config = { managerId: '', planIds: [] as string[], specialtyRubroId: '' };
  commitProgress = { running: false, done: 0, total: 0, current: '', created: 0, skipped: 0, failed: 0, failedList: [] as { name: string; error: string }[] };
  private readonly SUGGESTION_THRESHOLD = 0.8;
  private readonly ABBREVIATIONS: { [k: string]: string } = {
    GRAL: 'GENERAL',
    GEN: 'GENERAL',
  };
  constructor(private api: HipocratesImportService, private padronesService: SignoPadronesService, private cdr: ChangeDetectorRef) {}
  private advance(stepper: any) { this.cdr.detectChanges(); if (stepper) { stepper.next(); } }
  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Tiempo de espera agotado')), ms);
      promise.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
    });
  }
  normalize(value: string) { return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim(); }
  centerKey(name: string, locality: string, address: string) { return [this.normalize(name), this.normalize(locality), this.normalize(address).replace(/\bN(?:RO)?\s+(?=\d)/g, '')].join('|'); }
  async ngOnInit() {
    await this.run(async () => {
      this.meta = await this.api.request('GET', '/metadata');
      this.meta.specialties.sort((a, b) => a.concepto.localeCompare(b.concepto, 'es', { sensitivity: 'base' }));
      const saved = sessionStorage.getItem('hipocrates-import-id');
      if (saved) { try { this.setJob(await this.api.request('GET', '/' + saved)); this.report = await this.api.request('GET', '/' + saved + '/preview'); await this.loadCoverage(); if (!this.report.errors.length) { this.configured = this.provinceReviewed = this.locationsReviewed = true; } } catch { sessionStorage.removeItem('hipocrates-import-id'); } }
    });
  }
  ngOnDestroy() { this.stopGeo = true; this.destroyed = true; }
  async run(action: () => Promise<void>) {
    this.busy = true; this.error = '';
    try { await action(); } catch (e) { const err = e.error; this.error = (err && (err.message || err.response && err.response.message)) || e.message || 'No se pudo completar la operación.'; if (Array.isArray(this.error)) { this.error = (this.error as any).join('\n'); } }
    finally { this.busy = false; }
  }
  choose(event: any, complement = false) { const file = event.target.files[0] || null; if (complement) { this.complement = file; } else { this.file = file; } }
  setJob(job: any) {
    this.job = job; this.config = job.config; sessionStorage.setItem('hipocrates-import-id', job._id);
    if (!this.job.centerSpecialtyMap) { this.job.centerSpecialtyMap = {}; }
    if (!this.job.provinceMap) { this.job.provinceMap = {}; }
    if (!this.job.rubroMap) { this.job.rubroMap = {}; }
    this.terms = Object.keys(job.specialtyMap);
    this.terms.forEach(term => { const mapped = this.job.specialtyMap[term]; if (typeof mapped === 'string' && mapped.indexOf('new:') === 0) { this.newNames[term] = mapped.slice(4); } });
    this.refreshNames();
    this.computeSuggestions();
  }
  private stripParenthetical(value: string): string {
    return (value || '').replace(/\([^)]*\)/g, ' ');
  }
  private normalizedTokens(value: string): string[] {
    return this.normalize(this.stripParenthetical(value)).split(' ').filter(Boolean).map(t => this.ABBREVIATIONS[t] || t);
  }
  private root(value: string): string {
    const n = this.normalize(value);
    const suffixes = ['OS', 'AS', 'IA', 'O', 'A'];
    for (const suffix of suffixes) { if (n.endsWith(suffix)) { return n.slice(0, -suffix.length); } }
    return n;
  }
  private levenshtein(a: string, b: string): number {
    const m = a.length; const n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) { dp[i][0] = i; }
    for (let j = 0; j <= n; j++) { dp[0][j] = j; }
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      }
    }
    return dp[m][n];
  }
  private scoreSpecialty(term: string, concepto: string): number {
    const nt = this.normalize(term);
    const ns = this.normalize(concepto);
    if (!nt || !ns) { return 0; }
    if (nt === ns) { return 1; }
    if (this.root(term) === this.root(concepto)) { return 1; }
    const tt = this.normalizedTokens(term);
    const st = this.normalizedTokens(concepto);
    const intersection = st.filter(x => tt.includes(x)).length;
    if (st.length && intersection === st.length) { return 0.9; }
    const union = new Set([...tt, ...st]).size;
    const jaccard = union ? intersection / union : 0;
    const levSim = 1 - this.levenshtein(nt, ns) / Math.max(nt.length, ns.length);
    return Math.max(jaccard, levSim);
  }
  computeSuggestions() {
    this.suggestions = {};
    if (!this.meta || !this.job || !this.meta.specialties) { return; }
    this.terms.forEach(term => {
      const mapped = this.job.specialtyMap[term];
      if (mapped && mapped !== '') { return; }
      let best: { _id: string; concepto: string; score: number } | null = null;
      for (const s of this.meta.specialties) {
        const score = this.scoreSpecialty(term, s.concepto);
        if (score >= this.SUGGESTION_THRESHOLD && (!best || score > best.score)) {
          best = { _id: s._id, concepto: s.concepto, score };
        }
      }
      if (best) { this.suggestions[term] = { _id: best._id, concepto: best.concepto }; }
    });
  }
  suggestedSpecialty(term: string) { return this.suggestions[term] || null; }
  useSuggestion(term: string) { const s = this.suggestions[term]; if (s) { this.onSpecialtySelect(term, s._id); } }
  async loadCoverage() {
    this.coverage = null;
    if (!this.job) { return; }
    try {
      this.coverage = await this.api.request('GET', '/' + this.job._id + '/coverage');
      (this.coverage.centersWithoutSpecialties || []).forEach((c: any) => { if (!this.job.centerSpecialtyMap[c.key]) { this.job.centerSpecialtyMap[c.key] = []; } });
      this.computeCenterSuggestions();
    } catch { this.coverage = null; }
  }
  computeCenterSuggestions() {
    this.centerSuggestions = {};
    if (!this.meta || !this.meta.specialties || !this.coverage) { return; }
    (this.coverage.centersWithoutSpecialties || []).forEach((c: any) => {
      const list = this.centerSourceTerms(c).map(term => {
        if (['MEDICO', 'MEDICA'].includes(this.normalize(term))) {
          const mg = this.meta.specialties.find((s: any) => this.normalize(s.concepto) === 'MEDICINA GENERAL');
          return { term, id: mg ? mg._id : 'new:MEDICINA GENERAL' };
        }
        let best: { _id: string; score: number } | null = null;
        for (const s of this.meta.specialties) {
          const score = this.scoreSpecialty(term, s.concepto);
          if (score >= this.SUGGESTION_THRESHOLD && (!best || score > best.score)) { best = { _id: s._id, score }; }
        }
        return best ? { term, id: best._id } : null;
      }).filter((x): x is { term: string; id: string } => x !== null);
      if (list.length) { this.centerSuggestions[c.key] = list; }
    });
  }
  trackCenter(index: number, c: any) { return c.key; }
  trackSuggestion(index: number, s: any) { return s.term + '|' + s.id; }
  refreshNames() {
    this.names = Array.from(new Set<string>(this.job.rows.map(r => r.name))).sort();
    this.locations = Array.from(new Set<string>(this.job.rows.map(r => r.locality))).sort();
    this.locations.forEach(name => { if (!this.job.localityMap[name]) { this.job.localityMap[name] = { id: '', zoneId: '', newName: name }; } });
    const aliases: any = { SAP: 'SANATORIO ADVENTISTA DEL PLATA', CEM: 'CENTRO DE ESPECIALIDADES MEDICAS', CEES: 'CENTRO DE ESPECIALIDADES EN SALUD', IDEM: 'INSTITUTO DE ESPECIALIDADES MEDICAS' };
    this.names.forEach(name => { if (!this.renames[name]) { const prefix = aliases[this.normalize(name)]; const suggestion = prefix && this.job.institutions.find(i => this.normalize(i.name).startsWith(prefix)); this.renames[name] = suggestion ? suggestion.name : name; } });
  }
  async rename(name: string) {
    const target = (this.renames[name] || '').trim(); if (!target || target === name) { return; }
    this.job.rows.filter(r => r.name === name).forEach(r => {
      const oldKey = this.centerKey(name, r.locality, r.address); const newKey = this.centerKey(target, r.locality, r.address);
      const assigned = this.job.centerSpecialtyMap[oldKey];
      if (assigned) { this.job.centerSpecialtyMap[newKey] = Array.from(new Set([...(this.job.centerSpecialtyMap[newKey] || []), ...assigned])); delete this.job.centerSpecialtyMap[oldKey]; }
      r.name = target;
    });
    const linked = this.job.institutionMap[name] || [];
    this.job.institutionMap[target] = Array.from(new Set([...(this.job.institutionMap[target] || []), ...linked]));
    delete this.job.institutionMap[name]; this.refreshNames(); this.invalidateReview(); await this.loadCoverage();
  }
  invalidateReview() { this.configured = false; this.provinceReviewed = false; this.locationsReviewed = false; this.report = null; }
  invalidateLocations() { this.provinceReviewed = false; this.locationsReviewed = false; this.report = null; }
  countName(name: string) { return this.job.rows.filter(r => r.name === name).length; }
  async reloadJob() { await this.run(async () => { this.setJob(await this.api.request('GET', '/' + this.job._id)); this.report = await this.api.request('GET', '/' + this.job._id + '/preview'); await this.loadCoverage(); }); }
  get validConfig() { return this.config.managerId && this.config.planIds.length; }
  get distinctProvinces() { return this.job ? Array.from(new Set<string>(this.job.rows.map(r => r.province).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'es')) : []; }
  get distinctRubros() { return this.job ? Array.from(new Set<string>(this.job.rows.map(r => r.rubro || ''))).sort((a, b) => a.localeCompare(b, 'es')) : []; }
  get allProvincesResolved() { return this.distinctProvinces.every(p => !!(this.job.provinceMap && this.job.provinceMap[p])); }
  get allRubrosResolved() { return this.distinctRubros.every(r => !!(this.job.rubroMap && this.job.rubroMap[r])); }
  provinceIdForLocality(name: string) {
    const row = (this.job && this.job.rows || []).find(r => r.locality === name && r.province);
    const excelProv = row ? row.province : '';
    return excelProv && this.job.provinceMap ? (this.job.provinceMap[excelProv] || '') : '';
  }
  localitiesForName(name: string) {
    if (!this.meta) { return []; }
    const pid = this.provinceIdForLocality(name);
    return pid ? this.meta.localities.filter(l => l.provincia && l.provincia._id === pid) : this.meta.localities;
  }
  get filteredRows() { if (!this.job) { return []; } const q = this.normalize(this.filter); return this.job.rows.filter(r => !q || this.normalize(r.name + ' ' + r.locality + ' ' + r.row).includes(q)); }
  get visibleRows() { return this.filteredRows.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize); }
  get totalPages() { return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)); }
  get genericTerms() { return this.terms.filter(t => ['MEDICO', 'MEDICA'].includes(this.normalize(t))); }
  get specialtyTerms() {
    return this.terms.filter(t => !this.genericTerms.includes(t)).sort((a, b) => {
      const pendingA = !this.job?.specialtyMap[a];
      const pendingB = !this.job?.specialtyMap[b];
      return Number(pendingB) - Number(pendingA) || a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }
  get results() { const map = new Map(); if (this.job) { this.job.results.forEach(r => map.set(r.key, r)); } return Array.from(map.values()); }
  get resultCounts() {
    const results = this.job && this.job.results ? this.job.results : [];
    return {
      created: results.filter(r => r.status === 'Creado').length,
      skipped: results.filter(r => r.status === 'Existente omitido').length,
      failed: results.filter(r => r.status === 'Error').length,
    };
  }
  get missingRequired() { return this.meta ? this.meta.required.filter(n => !this.meta.specialties.some(s => this.normalize(s.concepto) === this.normalize(n))) : []; }
  get emptyCenters() { return this.coverage ? (this.coverage.centersWithoutSpecialties || []) : []; }
  get unresolvedCenters() { return this.emptyCenters.filter(c => !(this.job.centerSpecialtyMap[c.key] || []).length); }
  async excludeCenter(center: any) { await this.run(async () => { const rows = new Set(center.sourceRows); this.job.rows.forEach(r => { if (rows.has(r.row)) { r.excluded = true; } }); this.invalidateReview(); await this.loadCoverage(); }); }
  addExistingSpecialty(center: any, id: string) {
    if (!id) { return; }
    const list = this.job.centerSpecialtyMap[center.key] || [];
    if (list.indexOf(id) < 0) { this.job.centerSpecialtyMap[center.key] = [...list, id]; }
    if (this.centerSuggestions[center.key]) {
      this.centerSuggestions[center.key] = this.centerSuggestions[center.key].filter(s => s.id !== id);
      if (!this.centerSuggestions[center.key].length) { delete this.centerSuggestions[center.key]; }
    }
    this.invalidateReview();
  }
  autoProposeCenters() {
    if (!this.job || !this.coverage) { return; }
    const centers = this.coverage.centersWithoutSpecialties || [];
    Object.keys(this.centerSuggestions || {}).forEach(key => {
      const center = centers.find(c => c.key === key);
      if (!center) { return; }
      (this.centerSuggestions[key] || []).slice().forEach(s => {
        if (s && s.id) { this.addExistingSpecialty(center, s.id); }
      });
    });
  }
  addNewSpecialty(center: any, name: string) { const value = (name || '').trim(); if (!value) { return; } const list = this.job.centerSpecialtyMap[center.key] || (this.job.centerSpecialtyMap[center.key] = []); list.push('new:' + value); this.invalidateReview(); }
  centerSourceTerms(center: any): string[] {
    if (!this.job || !center || !center.sourceRows) { return []; }
    const rows = this.job.rows.filter((r: any) => center.sourceRows.includes(r.row));
    const terms = new Set<string>();
    rows.forEach((r: any) => (r.specialties || []).forEach((s: string) => { if (s && s.trim()) { terms.add(s.trim()); } }));
    return Array.from(terms);
  }
  removeSpecialty(center: any, value: string) { const list = this.job.centerSpecialtyMap[center.key] || []; const i = list.indexOf(value); if (i >= 0) { list.splice(i, 1); } this.invalidateReview(); }
  label(id: string) { const s = this.meta.specialties.find(x => x._id === id); return s ? s.concepto : id.replace(/^new:/, ''); }
  isNewMapping(term: string) { const mapped = this.job && this.job.specialtyMap[term]; return typeof mapped === 'string' && mapped.indexOf('new:') === 0; }
  newLabel(term: string) { return this.isNewMapping(term) ? this.job.specialtyMap[term].slice(4) : ''; }
  selectValue(term: string) { const mapped = this.job ? this.job.specialtyMap[term] : ''; return mapped || ''; }
  onSpecialtySelect(term: string, value: string) { this.job.specialtyMap[term] = value; this.invalidateReview(); }
  createForTerm(term: string, name: string) { const value = (name || '').trim(); if (!value) { return; } this.newNames[term] = value; this.job.specialtyMap[term] = 'new:' + value; this.invalidateReview(); }
  get draftSpecialties() {
    const names = new Set<string>();
    Object.keys((this.job && this.job.specialtyMap) || {}).forEach(term => { const v = this.job.specialtyMap[term]; if (typeof v === 'string' && v.indexOf('new:') === 0) { names.add(v.slice(4).trim()); } });
    Object.keys((this.job && this.job.centerSpecialtyMap) || {}).forEach(key => { (this.job.centerSpecialtyMap[key] || []).forEach((v: string) => { if (typeof v === 'string' && v.indexOf('new:') === 0) { names.add(v.slice(4).trim()); } }); });
    return Array.from(names).filter(n => n).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }
  async analyze() {
    if (!this.file || !this.validConfig) { return; }
    await this.run(async () => {
      this.setJob(await this.api.analyze(this.file, this.complement, this.config));
      if (!this.config.specialtyRubroId && this.job.rubroMap) {
        const first = Object.values(this.job.rubroMap).find(v => v);
        if (first) { this.config.specialtyRubroId = first as string; }
      }
      this.report = null; this.configured = false; this.provinceReviewed = false; this.locationsReviewed = false; await this.loadCoverage(); this.autoProposeCenters(); this.message = 'Archivo analizado. Revisá las filas y especialidades antes de continuar.';
    });
  }
  async save() { this.refreshNames(); this.job.config = this.config; this.setJob(await this.api.request('PUT', '/' + this.job._id, this.job)); this.report = null; }
  async nextConfig(stepper: any) { await this.run(async () => { await this.save(); await this.loadCoverage(); this.configured = true; this.advance(stepper); }); }
  async nextProvince(stepper: any) { await this.run(async () => { await this.save(); this.provinceReviewed = true; this.advance(stepper); }); }
  async preview(stepper?: any) { await this.run(async () => { await this.save(); this.report = await this.api.request('GET', '/' + this.job._id + '/preview'); if (!this.report.errors.length) { this.locationsReviewed = true; this.advance(stepper); } }); }
  localityChanged(name: string) { const mapping = this.job.localityMap[name]; const loc = this.meta.localities.find(l => l._id === mapping.id); if (loc) { mapping.zoneId = typeof loc.zona === 'string' ? loc.zona : loc.zona && loc.zona._id || ''; } }
  async geolocate() {
    this.geoRunning = true; this.stopGeo = false; this.geoProgress = 0; this.error = '';
    try {
      for (const c of this.report.centers) {
        if (this.stopGeo) { break; }
        if (!c.coordinates && !c.exists) {
          try { c.coordinates = await this.withTimeout(this.api.request('POST', '/' + this.job._id + '/geolocate', { key: c.key }), 15000); }
          catch { this.error = 'Algunas direcciones no pudieron geolocalizarse. Se conservarán sin coordenadas.'; }
          if (this.stopGeo) { break; }
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        this.geoProgress++;
      }
    } finally { this.geoRunning = false; }
    await this.refreshDraft();
  }
  async refreshDraft() {
    if (this.destroyed || !this.job) { return; }
    try { this.setJob(await this.api.request('GET', '/' + this.job._id)); this.report = await this.api.request('GET', '/' + this.job._id + '/preview'); } catch { /* conservar el estado actual */ }
  }
  async commit() {
    if (!this.report || this.job.status === 'completed') { return; }
    if (this.report.errors.length) {
      await Swal.fire({ title: 'Hay errores por resolver', html: this.report.errors.map(e => '• ' + e).join('<br>'), icon: 'error', confirmButtonText: 'Entendido' });
      return;
    }
    const centers = (this.report.centers || []) as any[];
    const confirmation = await Swal.fire({ title: '¿Crear los centros revisados?', text: `Se crearán ${centers.length} centros de a uno. Los existentes se omiten; si uno falla, se continúa con el siguiente.`, icon: 'question', showCancelButton: true, confirmButtonText: 'Confirmar importación', cancelButtonText: 'Volver' });
    if (!confirmation.isConfirmed) { return; }
    this.commitProgress = { running: true, done: 0, total: centers.length, current: '', created: 0, skipped: 0, failed: 0, failedList: [] };
    for (const c of centers) {
      this.commitProgress.current = c.name;
      try {
        const r = await this.api.commitOne(this.job._id, c.key);
        if (r && r.status === 'Creado') { this.commitProgress.created++; }
        else if (r && r.status === 'Existente omitido') { this.commitProgress.skipped++; }
        else { this.commitProgress.failed++; this.commitProgress.failedList.push({ name: c.name, error: (r && r.error) || 'Error desconocido' }); }
      } catch (e: any) {
        this.commitProgress.failed++;
        this.commitProgress.failedList.push({ name: c.name, error: (e && e.message) ? e.message : 'Error de comunicación con el servidor' });
      }
      this.commitProgress.done++;
      this.cdr.detectChanges();
    }
    this.commitProgress.running = false;
    let status = 'review';
    try { this.setJob(await this.api.finalize(this.job._id)); status = this.job.status; } catch { /* conservar */ }
    this.message = status === 'completed' ? 'Importación finalizada.' : 'Hay errores. Podés corregir y reintentar sin duplicar centros.';
    const p = this.commitProgress;
    const failedHtml = p.failedList.length
      ? `<div style="max-height:220px; overflow:auto; text-align:left; margin-top:8px;">${p.failedList.map(f => `<div style="border-bottom:1px solid #eee; padding:4px 0;"><b>${f.name}</b><br><span style="color:#d10505; font-size:0.85em;">${f.error}</span></div>`).join('')}</div>`
      : '';
    await Swal.fire({
      title: p.failed ? 'Importación con errores' : 'Importación finalizada',
      html: `<div style="text-align:left"><p><b>Centros creados:</b> ${p.created}</p><p><b>Centros omitidos (ya existían):</b> ${p.skipped}</p><p><b>Con errores:</b> ${p.failed}</p>${failedHtml}</div>`,
      icon: p.failed > 0 ? 'warning' : 'success',
      confirmButtonText: 'Entendido'
    });
    await this.saveHistory().catch(err => console.error('Error guardando historial Hipócrates:', err));
  }

  async saveHistory() {
    const results = this.job && this.job.results ? this.job.results : [];
    const centers = this.report && this.report.centers ? this.report.centers : [];
    const created = results.filter(r => r.status === 'Creado');
    const skipped = results.filter(r => r.status === 'Existente omitido');
    const failed = results.filter(r => r.status === 'Error');
    const geolocated = centers.filter(c => c.coordinates).length;
    const manager = this.meta && this.meta.managers ? this.meta.managers.find(m => m._id === this.config.managerId) : null;

    const historyRecord = {
      tipo: 'HIPOCRATES',
      gerenciadoraName: manager ? manager.nombre : 'Hipócrates',
      filename: this.job.fileName || 'sin_nombre.xlsx',
      totalRecords: this.job.rows ? this.job.rows.length : 0,
      totalCenters: centers.length,
      geolocatedCenters: geolocated,
      unmatchedCenters: centers.length - geolocated,
      createdCount: created.length,
      skippedCount: skipped.length,
      failedCount: failed.length,
      detallesCreados: created.map(r => r.name),
      detallesFallidos: failed.map(r => ({ nombre: r.name, domicilio: r.locality, error: r.error })),
    };

    await this.padronesService.saveImportHistory(historyRecord);
  }
  newImport() { sessionStorage.removeItem('hipocrates-import-id'); this.job = null; this.report = null; this.coverage = null; this.file = null; this.complement = null; this.configured = this.provinceReviewed = this.locationsReviewed = false; }
  downloadResults() { const blob = new Blob([JSON.stringify({ archivo: this.job.fileName, resultados: this.results, excluidas: this.job.rows.filter(r => r.excluded).map(r => r.row) }, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'resultado-hipocrates.json'; a.click(); URL.revokeObjectURL(url); }
}
